import { Identifiable } from '@approvalmax/types';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useCallback, useMemo, useState } from 'react';

import {
    changeQbooksAccountLineItemChecked,
    changeQbooksAccountLineItemsAccount,
    changeQbooksAccountLineItemsAllChecked,
    changeQbooksAccountLineItemsClass,
    changeQbooksAccountLineItemsCustomer,
    changeQbooksAccountLineItemsTaxCode,
    cloneQbooksAccountLineItems,
    cloneQbooksAccountLineItemsAll,
    removeQbooksAccountLineItems,
    removeQbooksAccountLineItemsAll,
} from '../../../../../actions';
import { ItemForEdit } from '../../../../../components';
import { getQBooksContext } from '../../../../../selectors/qbooks';
import { QBooksLineItemCustomerCell } from '../../../QBooksLineItemCustomerCell/QBooksLineItemCustomerCell';
import { QbooksLineItemDropdownCell } from '../../../QbooksLineItemDropdownCell/QbooksLineItemDropdownCell';
import { messages } from '../QBooksAccountLineItemsSection.messages';
import type { UseGroupEditItemsParams } from '../QBooksAccountLineItemsSection.types';

export const useGroupEditItems = (params: UseGroupEditItemsParams) => {
    const { accountLineItems, taxCodesList, accountVisible, taxVisible, classVisible, customerVisible } = params;

    const dispatch = useDispatch();

    const [isGroupEditPopupVisible, setIsGroupEditPopupVisible] = useState(false);
    const selectedItems = useMemo(() => accountLineItems.filter((item) => item.checked), [accountLineItems]);
    const qbooksContext = useSelector(getQBooksContext);

    const handleGroupRemove = useCallback(() => {
        if (selectedItems.length === accountLineItems.length) {
            dispatch(removeQbooksAccountLineItemsAll());
        } else {
            dispatch(removeQbooksAccountLineItems(selectedItems.map((lineItem) => lineItem.id)));
        }
    }, [accountLineItems.length, dispatch, selectedItems]);

    const handleGroupClone = useCallback(() => {
        if (selectedItems.length === accountLineItems.length) {
            dispatch(cloneQbooksAccountLineItemsAll());
        } else {
            dispatch(cloneQbooksAccountLineItems(selectedItems.map((item) => item.id)));
        }

        dispatch(changeQbooksAccountLineItemsAllChecked(false));
    }, [accountLineItems.length, dispatch, selectedItems]);

    const openGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(true);
    }, []);

    const closeGroupEdit = useCallback(() => {
        setIsGroupEditPopupVisible(false);
        selectedItems.forEach((lineItem) => dispatch(changeQbooksAccountLineItemChecked(lineItem.id, false)));
    }, [dispatch, selectedItems]);

    const groupItems = useMemo(() => {
        const res: ItemForEdit<Identifiable>[] = [];

        if (accountVisible) {
            res.push({
                id: 'account',
                name: messages.colNameAccount,
                render: ({ value, onChange }) => (
                    <QbooksLineItemDropdownCell
                        items={qbooksContext.accounts}
                        value={value}
                        onChange={onChange}
                        placeholder={messages.cellPlaceholderAccount}
                    />
                ),
                onSave: (newItem: domain.QBooksAccount) => {
                    dispatch(
                        changeQbooksAccountLineItemsAccount(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newItem
                        )
                    );
                },
            });
        }

        if (taxVisible) {
            res.push({
                id: 'taxRate',
                name: messages.colNameTaxCode,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksAccountLineItemsTaxCode(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QbooksLineItemDropdownCell
                            items={taxCodesList}
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderTaxCode}
                        />
                    );
                },
            });
        }

        if (classVisible) {
            res.push({
                id: 'classes',
                name: messages.colNameClass,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksAccountLineItemsClass(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QbooksLineItemDropdownCell
                            items={qbooksContext.classes}
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderClass}
                        />
                    );
                },
            });
        }

        if (customerVisible) {
            res.push({
                id: 'customer',
                name: messages.colNameCustomer,
                onSave: (newTax: domain.QBooksTaxCode) => {
                    dispatch(
                        changeQbooksAccountLineItemsCustomer(
                            selectedItems.map((accountLineItem) => accountLineItem.id),
                            newTax
                        )
                    );
                },
                render: ({ value, onChange }) => {
                    return (
                        <QBooksLineItemCustomerCell
                            value={value}
                            onChange={onChange}
                            placeholder={messages.cellPlaceholderCustomer}
                        />
                    );
                },
            });
        }

        return res;
    }, [
        accountVisible,
        classVisible,
        customerVisible,
        dispatch,
        qbooksContext.accounts,
        qbooksContext.classes,
        selectedItems,
        taxCodesList,
        taxVisible,
    ]);

    const isGroupRemoveVisible = useMemo(() => selectedItems.every((item) => !item.hideRemove), [selectedItems]);
    const isGroupCloneVisible = useMemo(() => selectedItems.every((item) => !item.hideClone), [selectedItems]);
    const isGroupEditVisible = groupItems.length > 0;
    const isShowGroupButtons =
        selectedItems.length > 0 && (isGroupRemoveVisible || isGroupCloneVisible || isGroupEditVisible);

    return {
        isShowGroupButtons,
        handleGroupRemove,
        handleGroupClone,
        openGroupEdit,
        closeGroupEdit,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isGroupCloneVisible,
        isGroupEditPopupVisible,
        groupItems,
    };
};
